import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { LanguageType, Locale } from '@/v1/main/domain/config';
import { ConfigService } from '@/v1/main/domain/config';

interface LanguageContextProps {
    language: LanguageType;
    locale: Locale;
    changeLanguage: (value: LanguageType) => void;
}

interface LanguageProviderProps {
    usecase: ConfigService;
    children: React.ReactNode;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(
    undefined
);

export const LanguageProvider = ({
    usecase,
    children,
}: LanguageProviderProps) => {
    const { i18n } = useTranslation();
    const language = i18n.language as LanguageType;
    usecase.changeLanguage(language);

    const locale = usecase.getLocale();
    const changeLanguage = (value: LanguageType) => {
        i18n.changeLanguage(value);
        usecase.changeLanguage(value);
    };

    return (
        <LanguageContext.Provider value={{ language, locale, changeLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
